<script>
import { ref, onMounted, onBeforeUnmount, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useRestoGLobalStore } from '@/stores/restoGlobalStore'
import { useCartGLobalStore } from '@/stores/cartGlobalStore'
import { createMollieSessionFn, placeOrderMollieSessionFn } from '@/services/api/restApiCalls'

const MOLLIE_V2_SCRIPT_ID = 'mollie-js-v2'

function loadMollieV2Script() {
  return new Promise((resolve, reject) => {
    if (window.MollieCheckout) {
      resolve(window.MollieCheckout)
      return
    }
    const existing = document.getElementById(MOLLIE_V2_SCRIPT_ID)
    if (existing) {
      existing.addEventListener('load', () => resolve(window.MollieCheckout))
      existing.addEventListener('error', reject)
      return
    }
    const script = document.createElement('script')
    script.id = MOLLIE_V2_SCRIPT_ID
    script.src = 'https://js.mollie.com/v2/mollie.js'
    script.async = true
    script.onload = () => resolve(window.MollieCheckout)
    script.onerror = reject
    document.head.appendChild(script)
  })
}

export default {
  props: {
    beforePlaceOrder: { type: Function, default: null }
  },

  emits: ['pay-button-state'],

  setup(props, { expose, emit }) {
    const router = useRouter()
    const restoStore = useRestoGLobalStore()
    const cartStore = useCartGLobalStore()

    const cardErrorMsg = ref('')
    const payButtonDisabled = ref(true)
    const isLoading = ref(true)
    const isProcessing = ref(false)
    const isUnmounted = ref(false)
    let cardComponent = null
    let checkout = null

    const finalizeOrder = async () => {
      const response = await placeOrderMollieSessionFn(restoStore?.RESTO_ID)
      if (!response || response?.status >= 400 || response?.error_code) {
        cardErrorMsg.value = response?.status_msg || 'Order creation failed.'
        isProcessing.value = false
        payButtonDisabled.value = false
        return
      }
      isProcessing.value = false
      router.push({
        name: 'order_status',
        params: { order_id: `${response.order_id}` }
      })
      cartStore.CART_DATA = ''
    }

    const initSession = async () => {
      try {
        const session = await createMollieSessionFn(restoStore?.RESTO_ID)
        if (!session || session?.status >= 400 || !session?.client_access_token) {
          cardErrorMsg.value = session?.status_msg || 'Unable to initialize payment.'
          isLoading.value = false
          payButtonDisabled.value = true
          return
        }

        await loadMollieV2Script()
        checkout = window.MollieCheckout(session.client_access_token, { locale: 'en_GB' })
        cardComponent = checkout.create('card')
        cardComponent.mount(document.getElementById('mollie-card-component-v2'))

        try {
          const applePayEl = document.getElementById('mollie-applepay-component-v2')
          if (applePayEl && checkout.create) {
            const applePayComponent = checkout.create('applepay')
            if (applePayComponent?.mount) {
              applePayComponent.mount(applePayEl)
              applePayComponent.on('complete', async () => {
                if (isUnmounted.value) return
                isProcessing.value = true
                payButtonDisabled.value = true
                if (props.beforePlaceOrder) {
                  const ready = await props.beforePlaceOrder()
                  if (!ready) {
                    isProcessing.value = false
                    payButtonDisabled.value = false
                    return
                  }
                }
                await finalizeOrder()
              })
            }
          }
        } catch (_) {
          // Apple Pay not enabled on profile or unsupported browser
        }

        cardComponent.on('complete', async () => {
          if (isUnmounted.value) return
          isProcessing.value = true
          payButtonDisabled.value = true
          if (props.beforePlaceOrder) {
            const ready = await props.beforePlaceOrder()
            if (!ready) {
              isProcessing.value = false
              payButtonDisabled.value = false
              return
            }
          }
          await finalizeOrder()
        })

        cardComponent.on('error', (error) => {
          cardErrorMsg.value = error?.message || 'Payment failed.'
          isProcessing.value = false
          payButtonDisabled.value = false
        })

        isLoading.value = false
        payButtonDisabled.value = false
      } catch (err) {
        cardErrorMsg.value = 'Unable to load Mollie payment form.'
        isLoading.value = false
        payButtonDisabled.value = true
      }
    }

    const handleMolliePayment = async () => {
      // V2 component handles payment inline; this satisfies parent button contract.
      if (payButtonDisabled.value || isProcessing.value) return
      cardErrorMsg.value = 'Complete payment using the card form above.'
    }

    onMounted(() => {
      isUnmounted.value = false
      initSession()
    })

    onBeforeUnmount(() => {
      isUnmounted.value = true
      if (cardComponent?.unmount) {
        cardComponent.unmount()
      }
    })

    watch(
      payButtonDisabled,
      (disabled) => {
        emit('pay-button-state', disabled !== false)
      },
      { immediate: true }
    )

    expose({
      handleMolliePayment,
      handleStripePayment: handleMolliePayment,
      payButtonDisabled
    })

    return {
      cardErrorMsg,
      payButtonDisabled,
      isLoading,
      isProcessing,
      handleMolliePayment
    }
  }
}
</script>

<template>
  <div class="mollie-payment-v2">
    <div v-if="isLoading" class="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">
      Preparing secure payment...
    </div>
    <div id="mollie-applepay-component-v2" class="mollie-applepay-component mb-3"></div>
    <div id="mollie-card-component-v2" class="mollie-card-component"></div>
    <div v-if="cardErrorMsg" class="text-sm font-medium text-red-600 dark:text-red-400 mt-2">
      {{ cardErrorMsg }}
    </div>
  </div>
</template>

<style scoped>
.mollie-applepay-component {
  min-height: 44px;
}

.mollie-card-component {
  border: 1px solid rgba(209, 213, 219, 1);
  border-radius: 0.375rem;
  padding: 16px;
  min-height: 180px;
  background: white;
}

html.dark .mollie-card-component {
  background: #374151;
  border-color: #4b5563;
}
</style>
